import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FiPlus, FiX, FiCheck, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { loadTable, getTableData } from '../db-viewer/data/dataAccess';
import { saveRow } from '../../lib/profileApi'; // routes self-edits to /api/auth/profile (works for account owners)
import '../bookkeeping/components/FamilyBusinessesPage.css';
import PhoneInput from '../../components/ui/PhoneInput';
import './ProfilePage.css';

// =============================================
// Shared helpers (same logic as FamilyBusinessesPage)
// =============================================

const PROTECTED_FIELDS = ['user_id', 'owner_id', 'user_type_id', 'password_hash',
  'created_at', 'signup_date', 'signup_ip', 'is_deleted', 'email_history',
  'failed_email_attempts', 'social_provider', 'social_provider_id', 'vpn_blocked',
  'address_verified'];

/** The logged-in user's id from the session (akt_session_user.id == user_id). */
function getSessionUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const id = JSON.parse(raw).id;
    return id != null ? Number(id) : null;
  } catch { return null; }
}

// "My Profile" edits the LOGGED-IN user's own row. Resolving via "first user with
// owner_id==null" was a multi-tenancy bug: with other owners present (e.g. seed
// shawn at uid 1), edits — including the Session Timeout — landed on the WRONG
// user, so the real user's setting never changed and they kept getting logged out.
function resolveCurrentUser(users: Record<string, any>[]): Record<string, any> | null {
  const sid = getSessionUserId();
  if (sid != null) {
    // eslint-disable-next-line eqeqeq
    const me = users.find(u => u.user_id == sid);
    if (me) return me;
  }
  return users.find(u => u.owner_id == null || u.owner_id === '') || users[0] || null;
}

function getAccountOwnerSync(): Record<string, any> | null {
  return resolveCurrentUser(getTableData('users'));
}

function filterSafeFields(fields: Record<string, any>): Record<string, any> {
  const safeFields: Record<string, any> = {};
  for (const [key, val] of Object.entries(fields)) {
    if (!PROTECTED_FIELDS.includes(key)) {
      safeFields[key] = val;
    }
  }
  return safeFields;
}

async function saveAccountOwner(userId: number | string, fields: Record<string, any>): Promise<boolean> {
  // updated_at is stamped by the server (DB clock), never sent from the client.
  try {
    await saveRow('users', userId, filterSafeFields(fields), 'user_id');
    return true;
  } catch {
    return false;
  }
}

function getCountriesFromData(countriesData: Record<string, any>[]): string[] {
  return countriesData.map(c => c.name).filter(Boolean).sort();
}

function getUserTypeInfoFromData(
  typesData: Record<string, any>[],
  userTypeId: number
): { user_type: string; hierarchy_level: number; description: string } | null {
  // eslint-disable-next-line eqeqeq
  const match = typesData.find(t => t.user_type_id == userTypeId);
  return match ? { user_type: match.user_type, hierarchy_level: match.hierarchy_level, description: match.description } : null;
}

function getUserRightsFromData(
  rightsData: Record<string, any>[],
  userTypeId: number
): { service_name: string; status: string }[] {
  return rightsData
    // eslint-disable-next-line eqeqeq
    .filter(r => r.user_type_id == userTypeId && r.is_active === 1)
    .sort((a, b) => (a.admin_service_id || 0) - (b.admin_service_id || 0))
    .map(r => ({ service_name: r.service_name, status: r.status }));
}

// =============================================
// Phone helpers
// =============================================

interface PhoneEntry {
  type: string;
  number: string;
}

const MOBILE_PRESETS = ['Primary Mobile', 'Secondary Mobile'];
const LANDLINE_PRESETS = ['Home Phone', 'Business Phone'];
const MAX_PHONES_PER_SECTION = 10;

function getMobileTypeOptions(phones: PhoneEntry[], currentIdx: number): string[] {
  const used = phones.filter((_, i) => i !== currentIdx).map(p => p.type);
  const options = MOBILE_PRESETS.filter(t => !used.includes(t));
  const currentType = phones[currentIdx]?.type;
  if (currentType && !MOBILE_PRESETS.includes(currentType) && !options.includes(currentType)) {
    options.push(currentType);
  }
  return options.length > 0 ? options : [currentType || `Mobile ${currentIdx + 1}`];
}

function getLandlineTypeOptions(phones: PhoneEntry[], currentIdx: number): string[] {
  const used = phones.filter((_, i) => i !== currentIdx).map(p => p.type);
  const options = LANDLINE_PRESETS.filter(t => !used.includes(t));
  const currentType = phones[currentIdx]?.type;
  if (currentType && !LANDLINE_PRESETS.includes(currentType) && !options.includes(currentType)) {
    options.push(currentType);
  }
  return options.length > 0 ? options : [currentType || `Phone ${currentIdx + 1}`];
}

function nextMobileLabel(phones: PhoneEntry[]): string {
  const used = phones.map(p => p.type);
  for (const preset of MOBILE_PRESETS) {
    if (!used.includes(preset)) return preset;
  }
  let n = 3;
  while (used.includes(`Mobile ${n}`)) n++;
  return `Mobile ${n}`;
}

function nextLandlineLabel(phones: PhoneEntry[]): string {
  const used = phones.map(p => p.type);
  for (const preset of LANDLINE_PRESETS) {
    if (!used.includes(preset)) return preset;
  }
  let n = 3;
  while (used.includes(`Phone ${n}`)) n++;
  return `Phone ${n}`;
}

// =============================================
// Validation
// =============================================

function isValidEmail(email: string): boolean {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function isValidPostalCode(code: string, country: string): { valid: boolean; hint: string } {
  if (!code) return { valid: true, hint: '' };
  const trimmed = code.trim();
  const c = country.toLowerCase();
  if (c.includes('canada')) {
    const valid = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(trimmed);
    return { valid, hint: 'Format: A1A 1A1 (e.g. M5V 3L9)' };
  }
  if (c.includes('united states') || c.includes('usa')) {
    const valid = /^\d{5}(-\d{4})?$/.test(trimmed);
    return { valid, hint: 'Format: 12345 or 12345-6789' };
  }
  if (c.includes('united kingdom') || c.includes('uk')) {
    const valid = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/.test(trimmed);
    return { valid, hint: 'Format: SW1A 1AA' };
  }
  if (c.includes('india')) {
    const valid = /^\d{6}$/.test(trimmed);
    return { valid, hint: 'Format: 6 digits (e.g. 400001)' };
  }
  if (c.includes('australia')) {
    const valid = /^\d{4}$/.test(trimmed);
    return { valid, hint: 'Format: 4 digits (e.g. 2000)' };
  }
  return { valid: true, hint: '' };
}

// =============================================
// Profile Page Component
// =============================================
const ProfilePage: React.FC = () => {
  const [owner, setOwner] = useState(() => getAccountOwnerSync());
  const [countriesData, setCountriesData] = useState<Record<string, any>[]>(() => getTableData('site_mstr_countries'));
  const [userTypesData, setUserTypesData] = useState<Record<string, any>[]>(() => getTableData('user_type'));
  const [userRightsData, setUserRightsData] = useState<Record<string, any>[]>(() => getTableData('user_rights'));
  const [loading, setLoading] = useState(true);

  const countries = useMemo(() => getCountriesFromData(countriesData), [countriesData]);
  const userId = owner?.user_id;

  // Session idle timeout (minutes) — sliding window, per-user. Saved immediately.
  const [idleMinutes, setIdleMinutes] = useState<number>(() => Number(owner?.session_idle_minutes) || 30);
  const [idleSaved, setIdleSaved] = useState(false);
  useEffect(() => {
    if (owner?.session_idle_minutes != null) setIdleMinutes(Number(owner.session_idle_minutes) || 30);
  }, [owner]);
  const handleIdleChange = useCallback(async (minutes: number) => {
    setIdleMinutes(minutes);
    setIdleSaved(false);
    if (!userId) return;
    try {
      await saveRow('users', userId, { session_idle_minutes: minutes }, 'user_id');
      setIdleSaved(true);
      setTimeout(() => setIdleSaved(false), 3000);
    } catch { /* surfaced elsewhere; keep the UI responsive */ }
  }, [userId]);

  // Load data from PostgreSQL API on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [usersRows, countriesRows, typesRows, rightsRows] = await Promise.all([
          loadTable('users'),
          loadTable('site_mstr_countries'),
          loadTable('user_type'),
          loadTable('user_rights'),
        ]);
        if (cancelled) return;
        const ownerRow = resolveCurrentUser(usersRows);
        setOwner(ownerRow);
        setCountriesData(countriesRows);
        setUserTypesData(typesRows);
        setUserRightsData(rightsRows);
      } catch {
        // Falls back to already-loaded localStorage data
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const [form, setForm] = useState(() => {
    if (!owner) return {} as Record<string, any>;
    return {
      first_name: owner.first_name || '',
      middle_name: owner.middle_name || '',
      last_name: owner.last_name || '',
      nickname: owner.nickname || '',
      screen_name: owner.screen_name || '',
      gender: owner.gender || '',
      user_name: owner.user_name || '',
      email: owner.email || '',
      user_type_id: owner.user_type_id || 8,
      country_code: owner.country_code || '',
      address_street: owner.address_street || '',
      address_city: owner.address_city || '',
      address_state: owner.address_state || '',
      address_postal_code: owner.address_postal_code || '',
      address_country: owner.address_country || '',
    };
  });

  const [mobilePhones, setMobilePhones] = useState<PhoneEntry[]>(() => {
    if (!owner) return [{ type: 'Primary Mobile', number: '' }];
    const phones: PhoneEntry[] = [];
    if (owner.mobile_number) phones.push({ type: 'Primary Mobile', number: owner.mobile_number });
    if (owner.secondary_mobile) phones.push({ type: 'Secondary Mobile', number: owner.secondary_mobile });
    const extras: PhoneEntry[] = Array.isArray(owner.extra_phones) ? owner.extra_phones : [];
    extras.filter(p => p.type?.startsWith('Mobile')).forEach(p => phones.push(p));
    if (phones.length === 0) phones.push({ type: 'Primary Mobile', number: '' });
    return phones;
  });

  const [landlinePhones, setLandlinePhones] = useState<PhoneEntry[]>(() => {
    if (!owner) return [{ type: 'Home Phone', number: '' }];
    const phones: PhoneEntry[] = [];
    if (owner.home_phone) phones.push({ type: 'Home Phone', number: owner.home_phone });
    if (owner.business_phone) phones.push({ type: 'Business Phone', number: owner.business_phone });
    const extras: PhoneEntry[] = Array.isArray(owner.extra_phones) ? owner.extra_phones : [];
    extras.filter(p => p.type?.startsWith('Phone')).forEach(p => phones.push(p));
    if (phones.length === 0) phones.push({ type: 'Home Phone', number: '' });
    return phones;
  });

  const [emailError, setEmailError] = useState('');
  const [emailVerifyState, setEmailVerifyState] = useState<'idle' | 'sent' | 'verified'>(
    owner?.email_verified === 1 ? 'verified' : 'idle'
  );
  const [verifyCode, setVerifyCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [addressVerified, setAddressVerified] = useState(owner?.address_verified === 1);
  const [addressVerifying, setAddressVerifying] = useState(false);
  const [postalError, setPostalError] = useState('');

  const [mailSameAsPhysical, setMailSameAsPhysical] = useState(() => owner?.mail_same_as_physical === 1);
  const [mailAddNew, setMailAddNew] = useState(() => {
    return !!(owner?.mail_address_street || owner?.mail_address_city) && owner?.mail_same_as_physical !== 1;
  });
  const [mailForm, setMailForm] = useState(() => ({
    mail_address_street: owner?.mail_address_street || '',
    mail_address_city: owner?.mail_address_city || '',
    mail_address_state: owner?.mail_address_state || '',
    mail_address_postal_code: owner?.mail_address_postal_code || '',
    mail_address_country: owner?.mail_address_country || '',
  }));
  const [mailPostalError, setMailPostalError] = useState('');

  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const userTypeInfo = useMemo(() => getUserTypeInfoFromData(userTypesData, form.user_type_id), [userTypesData, form.user_type_id]);
  const userTypeName = userTypeInfo?.user_type || 'Account Owner';
  const userRights = useMemo(() => getUserRightsFromData(userRightsData, form.user_type_id), [userRightsData, form.user_type_id]);

  const handleChange = useCallback((field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    if (field.startsWith('address_')) setAddressVerified(false);
  }, []);

  const formatPhone = (raw: string): string => {
    if (!raw) return '';
    return raw.replace(/[^+\d]/g, '');
  };

  const handleMobileChange = useCallback((idx: number, field: 'type' | 'number', value: string) => {
    setMobilePhones(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setSaved(false);
  }, []);

  const handleLandlineChange = useCallback((idx: number, field: 'type' | 'number', value: string) => {
    setLandlinePhones(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setSaved(false);
  }, []);

  const addMobile = useCallback(() => {
    setMobilePhones(prev => {
      if (prev.length >= MAX_PHONES_PER_SECTION) return prev;
      return [...prev, { type: nextMobileLabel(prev), number: '' }];
    });
  }, []);

  const addLandline = useCallback(() => {
    setLandlinePhones(prev => {
      if (prev.length >= MAX_PHONES_PER_SECTION) return prev;
      return [...prev, { type: nextLandlineLabel(prev), number: '' }];
    });
  }, []);

  const removeMobile = useCallback((idx: number) => {
    setMobilePhones(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setSaved(false);
  }, []);

  const removeLandline = useCallback((idx: number) => {
    setLandlinePhones(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setSaved(false);
  }, []);

  const handleMailChange = useCallback((field: string, value: string) => {
    setMailForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleMailPostalChange = useCallback((value: string) => {
    handleMailChange('mail_address_postal_code', value);
    if (value && mailForm.mail_address_country) {
      const { valid, hint } = isValidPostalCode(value, mailForm.mail_address_country);
      setMailPostalError(valid ? '' : hint);
    } else {
      setMailPostalError('');
    }
  }, [handleMailChange, mailForm.mail_address_country]);

  const handleMailSameToggle = useCallback((checked: boolean) => {
    setMailSameAsPhysical(checked);
    if (checked) {
      setMailAddNew(false);
      setMailForm({
        mail_address_street: form.address_street,
        mail_address_city: form.address_city,
        mail_address_state: form.address_state,
        mail_address_postal_code: form.address_postal_code,
        mail_address_country: form.address_country,
      });
      setMailPostalError('');
    }
  }, [form]);

  const handleMailAddNewToggle = useCallback((checked: boolean) => {
    setMailAddNew(checked);
    if (checked) {
      setMailSameAsPhysical(false);
      setMailForm({
        mail_address_street: '',
        mail_address_city: '',
        mail_address_state: '',
        mail_address_postal_code: '',
        mail_address_country: '',
      });
      setMailPostalError('');
    }
  }, []);

  const handleSendVerification = useCallback(() => {
    if (!isValidEmail(form.email)) {
      setEmailError('Please enter a valid email (e.g. user@domain.com or u@d.c)');
      return;
    }
    setEmailError('');
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setGeneratedCode(code);
    setEmailVerifyState('sent');
    setVerifyError('');
  }, [form.email]);

  const handleVerifyCode = useCallback(() => {
    if (verifyCode.trim() === generatedCode) {
      setEmailVerifyState('verified');
      setVerifyError('');
      if (userId) {
        saveRow('users', userId, { email_verified: 1 }, 'user_id').catch(() => {});
      }
    } else {
      setVerifyError('Invalid code. Please check the code from the verification link.');
    }
  }, [verifyCode, generatedCode, userId]);

  const handleVerifyAddress = useCallback(() => {
    setAddressVerifying(true);
    setTimeout(() => {
      const hasAllFields = form.address_street && form.address_city &&
        form.address_state && form.address_postal_code && form.address_country;
      if (hasAllFields) {
        setAddressVerified(true);
        if (userId) {
          saveRow('users', userId, { address_verified: 1 }, 'user_id').catch(() => {});
        }
      }
      setAddressVerifying(false);
    }, 1500);
  }, [form, userId]);

  const handlePostalChange = useCallback((value: string) => {
    handleChange('address_postal_code', value);
    if (value && form.address_country) {
      const { valid, hint } = isValidPostalCode(value, form.address_country);
      setPostalError(valid ? '' : hint);
    } else {
      setPostalError('');
    }
  }, [handleChange, form.address_country]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (!isValidEmail(form.email)) {
      setEmailError('Please enter a valid email (minimum format: u@d.c)');
      return;
    }
    setEmailError('');

    const cleanNumbers = [...mobilePhones, ...landlinePhones]
      .map(p => formatPhone(p.number))
      .filter(Boolean);
    const hasDuplicates = cleanNumbers.length !== new Set(cleanNumbers).size;
    if (hasDuplicates) {
      setPhoneError('Same phone number cannot be used in multiple fields. Each number must be unique.');
      return;
    }
    setPhoneError('');

    if (form.address_postal_code && form.address_country) {
      const { valid, hint } = isValidPostalCode(form.address_postal_code, form.address_country);
      if (!valid) {
        setPostalError(hint);
        return;
      }
    }

    const primaryMobile = mobilePhones.find(p => p.type === 'Primary Mobile');
    if (!primaryMobile || !primaryMobile.number.trim()) {
      setPhoneError('Primary Mobile is required.');
      return;
    }

    let nickname = form.nickname || null;
    if (!nickname && form.first_name && form.last_name) {
      nickname = (form.first_name[0] + form.last_name[0]).toUpperCase();
    }

    const mPrimary = mobilePhones.find(p => p.type === 'Primary Mobile');
    const mSecondary = mobilePhones.find(p => p.type === 'Secondary Mobile');
    const extraMobiles = mobilePhones
      .filter(p => p.type !== 'Primary Mobile' && p.type !== 'Secondary Mobile' && p.number.trim())
      .map(p => ({ type: p.type, number: formatPhone(p.number) }));
    const lHome = landlinePhones.find(p => p.type === 'Home Phone');
    const lBusiness = landlinePhones.find(p => p.type === 'Business Phone');
    const extraPhones = landlinePhones
      .filter(p => p.type !== 'Home Phone' && p.type !== 'Business Phone' && p.number.trim())
      .map(p => ({ type: p.type, number: formatPhone(p.number) }));

    const fieldsToUpdate: Record<string, any> = {
      first_name: form.first_name,
      middle_name: form.middle_name || null,
      last_name: form.last_name,
      nickname: nickname,
      screen_name: form.screen_name || null,
      gender: form.gender || null,
      mobile_number: formatPhone(mPrimary?.number || '') || null,
      secondary_mobile: formatPhone(mSecondary?.number || '') || null,
      home_phone: formatPhone(lHome?.number || '') || null,
      business_phone: formatPhone(lBusiness?.number || '') || null,
      extra_phones: [...extraMobiles, ...extraPhones].length > 0
        ? [...extraMobiles, ...extraPhones] : null,
      user_name: form.user_name,
      email: form.email,
      display_name: `${form.first_name} ${form.last_name}`.trim(),
      country_code: form.country_code || null,
      address_street: form.address_street || null,
      address_city: form.address_city || null,
      address_state: form.address_state || null,
      address_postal_code: form.address_postal_code || null,
      address_country: form.address_country || null,
      mail_same_as_physical: mailSameAsPhysical ? 1 : 0,
      mail_address_street: mailSameAsPhysical ? (form.address_street || null) : (mailForm.mail_address_street || null),
      mail_address_city: mailSameAsPhysical ? (form.address_city || null) : (mailForm.mail_address_city || null),
      mail_address_state: mailSameAsPhysical ? (form.address_state || null) : (mailForm.mail_address_state || null),
      mail_address_postal_code: mailSameAsPhysical ? (form.address_postal_code || null) : (mailForm.mail_address_postal_code || null),
      mail_address_country: mailSameAsPhysical ? (form.address_country || null) : (mailForm.mail_address_country || null),
    };

    if (mailAddNew && !mailSameAsPhysical && mailForm.mail_address_postal_code && mailForm.mail_address_country) {
      const { valid, hint } = isValidPostalCode(mailForm.mail_address_postal_code, mailForm.mail_address_country);
      if (!valid) {
        setMailPostalError(hint);
        return;
      }
    }

    const ok = await saveAccountOwner(userId, fieldsToUpdate);
    setSaved(ok);
    setSaveError(ok ? '' : 'Profile could not be saved. Your changes are NOT stored — please try again.');
    setTimeout(() => setSaved(false), 3000);
  }, [form, userId, mobilePhones, landlinePhones, mailSameAsPhysical, mailAddNew, mailForm]);

  // Re-initialize form when owner data arrives from API
  useEffect(() => {
    if (!owner) return;
    setForm({
      first_name: owner.first_name || '',
      middle_name: owner.middle_name || '',
      last_name: owner.last_name || '',
      nickname: owner.nickname || '',
      screen_name: owner.screen_name || '',
      gender: owner.gender || '',
      user_name: owner.user_name || '',
      email: owner.email || '',
      user_type_id: owner.user_type_id || 8,
      country_code: owner.country_code || '',
      address_street: owner.address_street || '',
      address_city: owner.address_city || '',
      address_state: owner.address_state || '',
      address_postal_code: owner.address_postal_code || '',
      address_country: owner.address_country || '',
    });
    setEmailVerifyState(owner.email_verified === 1 ? 'verified' : 'idle');
    setAddressVerified(owner.address_verified === 1);
    setMailSameAsPhysical(owner.mail_same_as_physical === 1);
    setMailAddNew(!!(owner.mail_address_street || owner.mail_address_city) && owner.mail_same_as_physical !== 1);
    setMailForm({
      mail_address_street: owner.mail_address_street || '',
      mail_address_city: owner.mail_address_city || '',
      mail_address_state: owner.mail_address_state || '',
      mail_address_postal_code: owner.mail_address_postal_code || '',
      mail_address_country: owner.mail_address_country || '',
    });

    // Re-init phones
    const mobiles: PhoneEntry[] = [];
    if (owner.mobile_number) mobiles.push({ type: 'Primary Mobile', number: owner.mobile_number });
    if (owner.secondary_mobile) mobiles.push({ type: 'Secondary Mobile', number: owner.secondary_mobile });
    const extras: PhoneEntry[] = Array.isArray(owner.extra_phones) ? owner.extra_phones : [];
    extras.filter(p => p.type?.startsWith('Mobile')).forEach(p => mobiles.push(p));
    if (mobiles.length === 0) mobiles.push({ type: 'Primary Mobile', number: '' });
    setMobilePhones(mobiles);

    const landlines: PhoneEntry[] = [];
    if (owner.home_phone) landlines.push({ type: 'Home Phone', number: owner.home_phone });
    if (owner.business_phone) landlines.push({ type: 'Business Phone', number: owner.business_phone });
    extras.filter(p => p.type?.startsWith('Phone')).forEach(p => landlines.push(p));
    if (landlines.length === 0) landlines.push({ type: 'Home Phone', number: '' });
    setLandlinePhones(landlines);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  // ---- Change Password ----
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleChangePassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (!pwForm.current) { setPwError('Please enter your current password'); return; }
    if (!pwForm.new) { setPwError('Please enter a new password'); return; }
    if (pwForm.new.length < 8) { setPwError('New password must be at least 8 characters'); return; }
    if (pwForm.new !== pwForm.confirm) { setPwError('New passwords do not match'); return; }
    if (pwForm.current === pwForm.new) { setPwError('New password must be different from current password'); return; }

    setPwLoading(true);
    try {
      const token = localStorage.getItem('akt_auth_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.new }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setPwError(data.message || 'Failed to change password');
      } else {
        setPwSuccess('Password changed successfully!');
        setPwForm({ current: '', new: '', confirm: '' });
        setTimeout(() => setPwSuccess(''), 5000);
      }
    } catch {
      setPwError('Unable to connect to server. Please try again.');
    } finally {
      setPwLoading(false);
    }
  }, [pwForm]);

  if (loading && !owner) {
    return <div className="fb-no-owner">Loading profile...</div>;
  }

  if (!owner || !userId) {
    return <div className="fb-no-owner">No account owner found. Please set up your account first.</div>;
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-avatar">{(form.first_name || 'U').charAt(0).toUpperCase()}</div>
        <div className="profile-header-info">
          <h2 className="profile-title">{form.first_name} {form.last_name}</h2>
          <span className="profile-subtitle">{userTypeName}{userTypeInfo ? ` · Level ${userTypeInfo.hierarchy_level}` : ''}</span>
        </div>
      </div>

      {/* eslint-disable-next-line eqeqeq */}
      {form.user_type_id != 8 && form.user_type_id != 9 && (
        <div className="fb-user-type-bar">
          <div className="fb-user-type-badge">
            <span className="fb-user-type-label">Account Type:</span>
            <span className="fb-user-type-value">{userTypeName}</span>
            {userTypeInfo && (
              <span className="fb-user-type-level">Level {userTypeInfo.hierarchy_level}</span>
            )}
          </div>
          {userTypeInfo && (
            <p className="fb-user-type-desc">{userTypeInfo.description}</p>
          )}
        </div>
      )}

      <form className="fb-form" onSubmit={handleSubmit}>
        <fieldset className="fb-fieldset">
          <legend>Your Legal Name *</legend>
          <div className="fb-name-row">
            <input type="text" placeholder="First Name" value={form.first_name}
              onChange={e => handleChange('first_name', e.target.value)} required className="fb-input" />
            <input type="text" placeholder="Middle Name" value={form.middle_name}
              onChange={e => handleChange('middle_name', e.target.value)} className="fb-input" />
            <input type="text" placeholder="Last Name" value={form.last_name}
              onChange={e => handleChange('last_name', e.target.value)} required className="fb-input" />
          </div>
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Session Timeout</legend>
          <p className="profile-hint" style={{ margin: '0 0 10px', color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Log out automatically after this much inactivity. Active use keeps you signed in; the timer resets on each action.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select
              className="fb-input"
              style={{ maxWidth: 240 }}
              value={idleMinutes}
              onChange={e => handleIdleChange(Number(e.target.value))}
            >
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={240}>4 hours</option>
              <option value={480}>8 hours</option>
            </select>
            {idleSaved && <span className="fb-saved-msg" style={{ color: '#15803d' }}>✓ Saved</span>}
          </div>
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Nickname or Acronym for Name</legend>
          <input type="text" placeholder="e.g. SM for Sat Mehta (used in DMS & Bookkeeping)"
            value={form.nickname} onChange={e => handleChange('nickname', e.target.value)}
            maxLength={20} className="fb-input fb-input-full" />
          <small className="fb-field-hint">Must be unique within your account. Auto-suggested from initials if left blank.</small>
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Screen Name</legend>
          <input type="text" placeholder="Your public screen name (used in discussions)"
            value={form.screen_name} onChange={e => handleChange('screen_name', e.target.value)}
            maxLength={100} className="fb-input fb-input-full" />
          <small className="fb-field-hint">Must be unique across the platform. No profanity or system-reserved words.</small>
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Gender *</legend>
          <select value={form.gender} onChange={e => handleChange('gender', e.target.value)}
            required className="fb-input fb-input-full">
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Non-binary">Non-binary</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Mobile Phone(s) *</legend>
          <small className="fb-field-hint">Primary Mobile is required. Verified via SMS. No number can be shared across fields.</small>
          {mobilePhones.map((phone, idx) => (
            <div key={idx} className="fb-phone-entry">
              <select value={phone.type} onChange={e => handleMobileChange(idx, 'type', e.target.value)}
                className="fb-input fb-phone-type-select">
                {getMobileTypeOptions(mobilePhones, idx).map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
                {!getMobileTypeOptions(mobilePhones, idx).includes(phone.type) && (
                  <option value={phone.type}>{phone.type}</option>
                )}
              </select>
              <PhoneInput value={phone.number} onChange={v => handleMobileChange(idx, 'number', v)}
                defaultCountry={form.address_country}
                required={phone.type === 'Primary Mobile'} className="fb-phone-number-input" inputClassName="fb-input" />
              {mobilePhones.length > 1 && (
                <button type="button" className="fb-btn-icon fb-btn-danger" onClick={() => removeMobile(idx)} title="Remove">
                  <FiX size={16} />
                </button>
              )}
            </div>
          ))}
          {mobilePhones.length < MAX_PHONES_PER_SECTION && (
            <button type="button" className="fb-btn-add-phone" onClick={addMobile}>
              <FiPlus size={14} /> Add another mobile
            </button>
          )}
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Other Phone(s)</legend>
          <small className="fb-field-hint">Home Phone verified via phone call. No mobiles here — use mobile section above.</small>
          {landlinePhones.map((phone, idx) => (
            <div key={idx} className="fb-phone-entry">
              <select value={phone.type} onChange={e => handleLandlineChange(idx, 'type', e.target.value)}
                className="fb-input fb-phone-type-select">
                {getLandlineTypeOptions(landlinePhones, idx).map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
                {!getLandlineTypeOptions(landlinePhones, idx).includes(phone.type) && (
                  <option value={phone.type}>{phone.type}</option>
                )}
              </select>
              <PhoneInput value={phone.number} onChange={v => handleLandlineChange(idx, 'number', v)}
                defaultCountry={form.address_country}
                className="fb-phone-number-input" inputClassName="fb-input" />
              {landlinePhones.length > 1 && (
                <button type="button" className="fb-btn-icon fb-btn-danger" onClick={() => removeLandline(idx)} title="Remove">
                  <FiX size={16} />
                </button>
              )}
            </div>
          ))}
          {landlinePhones.length < MAX_PHONES_PER_SECTION && (
            <button type="button" className="fb-btn-add-phone" onClick={addLandline}>
              <FiPlus size={14} /> Add another phone
            </button>
          )}
        </fieldset>
        {phoneError && <p className="fb-phone-error">{phoneError}</p>}

        <fieldset className="fb-fieldset">
          <legend>User Name</legend>
          <input type="text" placeholder="firstname.lastname (e.g. john.smith)" value={form.user_name}
            onChange={e => handleChange('user_name', e.target.value)} className="fb-input fb-input-full" />
          <small className="fb-field-hint">Format: 4+ letters/numbers, a dot, then 4+ letters/numbers</small>
        </fieldset>

        <fieldset className="fb-fieldset">
          <legend>Email *</legend>
          <div className="fb-email-row">
            <input type="email" placeholder="user@domain.com (minimum: u@d.c)" value={form.email}
              onChange={e => {
                handleChange('email', e.target.value);
                setEmailError('');
                if (emailVerifyState === 'verified') setEmailVerifyState('idle');
              }}
              required className="fb-input fb-email-input" />
            {emailVerifyState === 'verified' ? (
              <span className="fb-verified-badge"><FiCheck size={14} /> Verified</span>
            ) : (
              <button type="button" className="fb-btn-verify" onClick={handleSendVerification}>
                Verify Email
              </button>
            )}
          </div>
          {emailError && <span className="fb-field-error">{emailError}</span>}
          <small className="fb-field-hint">We'll send a link to this email. Click the link to see a code, then enter it below.</small>
          {emailVerifyState === 'sent' && (
            <div className="fb-verify-code-section">
              <p className="fb-verify-instruction">
                A verification link has been sent to <strong>{form.email}</strong>.
                Click the link in your email — it will open our site and show a 6-digit code. Enter that code below:
              </p>
              <div className="fb-verify-code-row">
                <input type="text" placeholder="Enter 6-digit code" value={verifyCode}
                  onChange={e => { setVerifyCode(e.target.value); setVerifyError(''); }}
                  maxLength={6} className="fb-input fb-verify-code-input" />
                <button type="button" className="fb-btn-verify" onClick={handleVerifyCode}>
                  Confirm Code
                </button>
              </div>
              {verifyError && <span className="fb-field-error">{verifyError}</span>}
              <small className="fb-field-hint" style={{ color: '#1a7f37', fontWeight: 600 }}>
                (Dev mode — your code is: {generatedCode})
              </small>
            </div>
          )}
        </fieldset>

        <fieldset className="fb-fieldset fb-address-fieldset">
          <legend>Address *</legend>
          <small className="fb-field-hint fb-address-hint">
            Country auto-detected from your IP. Address verified via Google Places (or equivalent by country).
          </small>
          <div className="fb-address-stack">
            <select value={form.address_country}
              onChange={e => { handleChange('address_country', e.target.value); setPostalError(''); }}
              required className="fb-input fb-input-full">
              <option value="">Select Country *</option>
              {countries.map(c => (<option key={c} value={c}>{c}</option>))}
            </select>
            <input type="text" placeholder="Street Address" value={form.address_street}
              onChange={e => handleChange('address_street', e.target.value)} className="fb-input fb-input-full" />
            <input type="text" placeholder="City / Town / Village *" value={form.address_city}
              onChange={e => handleChange('address_city', e.target.value)} required className="fb-input fb-input-full" />
            <input type="text" placeholder="State / Province *" value={form.address_state}
              onChange={e => handleChange('address_state', e.target.value)} required className="fb-input fb-input-full" />
            <div className="fb-postal-row">
              <input type="text" placeholder="Zip Code / Postal Code *" value={form.address_postal_code}
                onChange={e => handlePostalChange(e.target.value)} required className="fb-input fb-postal-input" />
              {postalError && <span className="fb-field-error fb-postal-error">{postalError}</span>}
            </div>
          </div>
          <div className="fb-address-verify-row">
            {addressVerified ? (
              <span className="fb-verified-badge"><FiCheck size={14} /> Address Verified</span>
            ) : (
              <button type="button" className="fb-btn-verify" onClick={handleVerifyAddress}
                disabled={addressVerifying || !form.address_street || !form.address_city || !form.address_country}>
                {addressVerifying ? 'Verifying...' : 'Verify Address'}
              </button>
            )}
            <small className="fb-field-hint">Uses Google Places API (or equivalent) based on your country.</small>
          </div>
        </fieldset>

        <fieldset className="fb-fieldset fb-address-fieldset fb-mailing-fieldset">
          <legend>Mailing Address</legend>
          <div className="fb-mailing-checkboxes">
            <label className="fb-checkbox-label">
              <input type="checkbox" checked={mailSameAsPhysical}
                onChange={e => handleMailSameToggle(e.target.checked)} />
              Same as Physical
            </label>
            <label className="fb-checkbox-label">
              <input type="checkbox" checked={mailAddNew}
                onChange={e => handleMailAddNewToggle(e.target.checked)} />
              Add New
            </label>
          </div>
          {mailSameAsPhysical && (
            <small className="fb-field-hint fb-mail-same-hint">
              Mailing address will use your physical address above.
            </small>
          )}
          {mailAddNew && !mailSameAsPhysical && (
            <div className="fb-address-stack">
              <select value={mailForm.mail_address_country}
                onChange={e => { handleMailChange('mail_address_country', e.target.value); setMailPostalError(''); }}
                className="fb-input fb-input-full">
                <option value="">Select Country</option>
                {countries.map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
              <input type="text" placeholder="Street Address" value={mailForm.mail_address_street}
                onChange={e => handleMailChange('mail_address_street', e.target.value)} className="fb-input fb-input-full" />
              <input type="text" placeholder="City / Town / Village" value={mailForm.mail_address_city}
                onChange={e => handleMailChange('mail_address_city', e.target.value)} className="fb-input fb-input-full" />
              <input type="text" placeholder="State / Province" value={mailForm.mail_address_state}
                onChange={e => handleMailChange('mail_address_state', e.target.value)} className="fb-input fb-input-full" />
              <div className="fb-postal-row">
                <input type="text" placeholder="Zip Code / Postal Code" value={mailForm.mail_address_postal_code}
                  onChange={e => handleMailPostalChange(e.target.value)} className="fb-input fb-postal-input" />
                {mailPostalError && <span className="fb-field-error fb-postal-error">{mailPostalError}</span>}
              </div>
            </div>
          )}
        </fieldset>

        <div className="fb-form-actions">
          <button type="submit" className="fb-btn-submit">Save Profile</button>
          {saved && <span className="fb-saved-msg">Profile saved successfully!</span>}
          {saveError && <span className="fb-field-error" role="alert">{saveError}</span>}
        </div>
      </form>

      {/* eslint-disable-next-line eqeqeq */}
      {userRights.length > 0 && form.user_type_id != 8 && form.user_type_id != 9 && (
        <div className="fb-rights-section">
          <h4 className="fb-rights-title">
            Your Service Access
            <span className="fb-rights-source">(from user_rights table)</span>
          </h4>
          <div className="fb-rights-grid">
            {userRights.map(r => (
              <div key={r.service_name} className={`fb-right-chip ${r.status === 'Yes' ? 'active' : 'inactive'}`}>
                <span className="fb-right-dot">{r.status === 'Yes' ? '✓' : '✗'}</span>
                {r.service_name}
              </div>
            ))}
          </div>
          <p className="fb-rights-note">
            To change service access, go to Data &amp; Backups &gt; User Rights table.
          </p>
        </div>
      )}

      {/* ===== Change Password ===== */}
      <div className="profile-password-section">
        <h3 className="profile-password-title"><FiLock size={18} /> Change Password</h3>
        <form className="profile-password-form" onSubmit={handleChangePassword}>
          <div className="profile-pw-field">
            <label>Current Password</label>
            <div className="profile-pw-input-wrap">
              <input
                type={showCurrentPw ? 'text' : 'password'}
                value={pwForm.current}
                onChange={e => { setPwForm(prev => ({ ...prev, current: e.target.value })); setPwError(''); }}
                placeholder="Enter current password"
                className="fb-input fb-input-full"
                autoComplete="current-password"
              />
              <button type="button" className="profile-pw-toggle" onClick={() => setShowCurrentPw(v => !v)} tabIndex={-1}>
                {showCurrentPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </div>
          <div className="profile-pw-field">
            <label>New Password</label>
            <div className="profile-pw-input-wrap">
              <input
                type={showNewPw ? 'text' : 'password'}
                value={pwForm.new}
                onChange={e => { setPwForm(prev => ({ ...prev, new: e.target.value })); setPwError(''); }}
                placeholder="Minimum 8 characters"
                className="fb-input fb-input-full"
                autoComplete="new-password"
              />
              <button type="button" className="profile-pw-toggle" onClick={() => setShowNewPw(v => !v)} tabIndex={-1}>
                {showNewPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </div>
          <div className="profile-pw-field">
            <label>Confirm New Password</label>
            <input
              type={showNewPw ? 'text' : 'password'}
              value={pwForm.confirm}
              onChange={e => { setPwForm(prev => ({ ...prev, confirm: e.target.value })); setPwError(''); }}
              placeholder="Re-enter new password"
              className="fb-input fb-input-full"
              autoComplete="new-password"
            />
          </div>
          {pwError && <p className="profile-pw-error">{pwError}</p>}
          {pwSuccess && <p className="profile-pw-success">{pwSuccess}</p>}
          <button type="submit" className="fb-btn-submit" disabled={pwLoading}>
            {pwLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
